import type { Token } from '@integration-layer/configs/commercelayer'
import { isTokenValid } from '@integration-layer/configs/commercelayer'
import {
  clearStoredOobCheckoutAccessToken,
  getCommerceLayerJwtExpiry,
  readStoredOobCheckoutAccessToken,
  writeStoredOobCheckoutAccessToken,
  writeStoredOobCheckoutOrderId,
} from '@integration-layer/utils/oobCheckoutToken'
import {
  findOobOperatorTag,
  hasOobOperatorTag,
} from '@integration-layer/utils/oobTags'

interface UserWithMetadata {
  email?: string
  user_metadata?: {
    customer_tags?: string[]
  }
}

interface OrderMetadata {
  salesforceId?: string
  orderVisibile?: boolean
  [key: string]: unknown
}

export interface OnBehalfState {
  isActive: boolean
  targetCustomerEmail: string | null
  targetCustomerId: string | null
  agentEmail: string | null
}

export interface SendOobOtpResponse {
  sent: boolean
}

export interface OobAddress {
  id?: string
  first_name?: string
  last_name?: string
  line_1?: string
  line_2?: string
  city?: string
  state_code?: string
  zip_code?: string
  country_code?: string
  phone?: string
  [key: string]: unknown
}

export const useOrderOnBehalf = () => {
  const { user, loggedIn, fetch: fetchUserSession } = useUserSession()
  const { ts } = useI18n()

  // ─── Backward-compat state (used by useCL.ts) ───────────────────────────
  const onBehalfState = useState<OnBehalfState>('orderOnBehalf', () => ({
    isActive: false,
    targetCustomerEmail: null,
    targetCustomerId: null,
    agentEmail: null,
  }))

  const onBehalfToken = useState<Token | null>('onBehalfToken', () => null)

  // ─── Operator detection ──────────────────────────────────────────────────

  /**
   * Explicit operator flag set by initOperatorMode() after confirming tags.
   * Using explicit state (rather than a computed) prevents a race condition where
   * user_metadata.customer_tags hasn't hydrated yet at component setup time.
   */
  const isOperatorModeExplicit = useState<boolean>(
    'oobIsOperatorExplicit',
    () => false
  )
  const isOperatorModeResolved = useState<boolean>(
    'oobOperatorModeResolved',
    () => false
  )

  const isOperatorMode = computed(() => {
    if (isOperatorModeResolved.value) return isOperatorModeExplicit.value
    if (!loggedIn.value || !user.value) return false
    const tags: string[] =
      (user.value as UserWithMetadata)?.user_metadata?.customer_tags ?? []
    return hasOobOperatorTag(tags)
  })

  /** First OOB operator tag found, e.g. "oob_odity" (see utils/oobTags.ts) */
  const operatorTag = computed<string | null>(() => {
    if (!loggedIn.value || !user.value) return null
    const tags: string[] =
      (user.value as UserWithMetadata)?.user_metadata?.customer_tags ?? []
    return findOobOperatorTag(tags)
  })

  /** Alias kept for CheckoutEmailValidationStep backward compat */
  const canOrderOnBehalf = isOperatorMode

  // ─── OOB session state ───────────────────────────────────────────────────
  const oobCustomerEmail = useState<string>('oobCustomerEmail', () => '')
  const oobCustomerCLId = useState<string | null>('oobCustomerCLId', () => null)
  /** Salesforce CRM id of the searched customer (from CL metadata), not the operator. */
  const oobCustomerCrmId = useState<string | null>(
    'oobCustomerCrmId',
    () => null
  )
  const oobCustomerIsRegistered = useState<boolean>(
    'oobCustomerIsRegistered',
    () => false
  )
  /** UI radio only — when true, patches order metadata like setCartLangAndSalesforceId. */
  const includeInOrderHistory = useState<boolean>(
    'oobIncludeInHistory',
    () => false
  )

  const otpSent = useState<boolean>('oobOtpSent', () => false)
  const otpVerified = useState<boolean>('oobOtpVerified', () => false)

  const isLoading = useState<boolean>('oobLoading', () => false)
  const error = useState<string | null>('oobError', () => null)

  // ─── Token helpers (used by useCL.ts for customer link flow) ─────────────
  const getToken = async (): Promise<string> => {
    if (onBehalfState.value.isActive && onBehalfToken.value) {
      if (isTokenValid(onBehalfToken.value)) {
        return onBehalfToken.value.accessToken
      }
    }
    throw new Error('No active OOB customer session')
  }

  // ─── Methods ─────────────────────────────────────────────────────────────

  /**
   * Guest OOB checkout link — customer is not the operator.
   * Skips initOperatorMode() and prevents operator UI from showing.
   */
  const skipOperatorModeForGuestCheckout = () => {
    isOperatorModeExplicit.value = false
    isOperatorModeResolved.value = true
  }

  const resolveOperatorModeFromCl = async (
    userEmail: string
  ): Promise<boolean> => {
    try {
      const { hasTag } = await $fetch<{ hasTag: boolean }>(
        '/api/cl/oob/checkOperatorTag',
        { method: 'POST', body: { email: userEmail } }
      )
      // Server may have patched customer_tags — refresh the client session.
      await fetchUserSession()
      return hasTag
    } catch {
      return false
    }
  }

  /**
   * Resolves operator mode from the session or via API call.
   * Must be awaited in the page async setup (before components render) so that
   * isOperatorModeResolved is true when CheckoutEmailValidation makes its navigation decision.
   */
  const initOperatorMode = async (): Promise<void> => {
    isOperatorModeResolved.value = false

    if (!loggedIn.value || !user.value) {
      isOperatorModeExplicit.value = false
      isOperatorModeResolved.value = true
      return
    }

    const sessionTags = (user.value as UserWithMetadata)?.user_metadata
      ?.customer_tags
    if (Array.isArray(sessionTags) && !hasOobOperatorTag(sessionTags)) {
      isOperatorModeExplicit.value = false
      isOperatorModeResolved.value = true
      return
    }

    const userEmail = (user.value as UserWithMetadata)?.email
    if (!userEmail) {
      isOperatorModeExplicit.value = false
      isOperatorModeResolved.value = true
      return
    }

    isOperatorModeExplicit.value = await resolveOperatorModeFromCl(userEmail)
    isOperatorModeResolved.value = true
  }

  const searchOobCustomer = async (
    email: string
  ): Promise<{
    isRegistered: boolean
    clId: string | null
  }> => {
    isLoading.value = true
    error.value = null

    try {
      // 1. Look up the customer in Commerce Layer (also validates the operator tag)
      const clData = await $fetch<{
        clId: string | null
        crmId: string | null
      }>('/api/cl/oob/searchCustomer', { method: 'POST', body: { email } })

      const isRegistered = Boolean(clData.crmId)

      const { language: checkoutLanguage } = useRouteHelper()
      await $fetch<SendOobOtpResponse>('/api/cl/oob/sendOtpOrderOnBehalf', {
        method: 'POST',
        body: { email, language: checkoutLanguage },
      })
      oobCustomerEmail.value = email
      oobCustomerIsRegistered.value = isRegistered
      oobCustomerCLId.value = clData.clId
      oobCustomerCrmId.value = clData.crmId ?? null
      otpSent.value = true
      otpVerified.value = false

      return { isRegistered, clId: clData.clId }
    } catch (err) {
      error.value = ts('checkout.oob.searchError')
      console.debug('Customer search error:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Validates OTP entered by the operator on behalf of the customer.
   * Verification is performed server-side — the OTP value is never held in client state.
   */
  const verifyOtp = async (email: string, code: string): Promise<boolean> => {
    isLoading.value = true
    error.value = null

    try {
      const result = await $fetch<{ valid: boolean; reason?: string }>(
        '/api/cl/oob/verifyOtpOrderOnBehalf',
        { method: 'POST', body: { email, code } }
      )

      otpVerified.value = result.valid
      if (!result.valid) {
        error.value =
          result.reason === 'expired'
            ? ts('checkout.oob.otpExpired')
            : result.reason === 'too_many_attempts'
              ? ts('checkout.oob.otpTooManyAttempts')
              : ts('checkout.oob.otpInvalid')
      }
      return result.valid
    } catch {
      error.value = ts('checkout.oob.otpInvalid')
      otpVerified.value = false
      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * When the registered customer opts in to order history, attach their Salesforce id
   * (from searchCustomer / oobCustomerCrmId) — never the CS operator session.
   */
  const applyOobOrderHistoryMetadata = async (
    includeInHistory: boolean
  ): Promise<void> => {
    const cart = useOrder('cart')
    const existingMetadata = (cart.order.value?.metadata ?? {}) as OrderMetadata
    const crmId = oobCustomerCrmId.value

    if (includeInHistory && !crmId) return

    const salesforceIdUnchanged = includeInHistory
      ? existingMetadata.salesforceId === crmId
      : !existingMetadata.salesforceId

    if (
      existingMetadata.orderVisibile === includeInHistory &&
      salesforceIdUnchanged
    ) {
      return
    }

    const rest = { ...existingMetadata }
    delete rest.salesforceId

    await cart.updateOrder({
      metadata: {
        ...rest,
        orderVisibile: includeInHistory,
        ...(includeInHistory && crmId ? { salesforceId: crmId } : {}),
      },
    })
  }

  /**
   * Called when the operator leaves the customer-search step (after OTP + visibility choice).
   * Clears any CS session addresses, then patches Salesforce visibility metadata.
   *
   * Note: customer_email is intentionally NOT set here. During the operator
   * session $cl uses the operator's *customer* token, so Commerce Layer forces
   * the order's customer to the operator and ignores customer_email (see CL docs
   * "Logged customers"). The order is re-associated to the OOB client server-side
   * with the integration token at link creation (prepareOobCheckoutLink).
   */
  const completeOobCustomerSearchStep = async (): Promise<void> => {
    if (!oobCustomerEmail.value || !otpVerified.value) return

    const { removeAddresses } = useCart()

    await removeAddresses()

    if (oobCustomerIsRegistered.value) {
      await applyOobOrderHistoryMetadata(includeInOrderHistory.value)
    }
  }

  /** Calls createShareToken and returns the shareable checkout URL. */
  const generateShareableLink = async (orderId: string): Promise<string> => {
    const { countryAndLanguage, country } = useRouteHelper()

    const result = await $fetch<{ url: string }>(
      '/api/cl/oob/createShareToken',
      {
        method: 'POST',
        body: {
          orderId,
          oobCustomerEmail: oobCustomerEmail.value,
          country,
          locale: countryAndLanguage,
        },
      }
    )
    return result.url
  }

  /** Sends a checkout email to the customer with the shareable link. */
  const sendCheckoutEmailToClient = async (orderId: string): Promise<void> => {
    const {
      language: checkoutLanguage,
      country,
      countryAndLanguage,
    } = useRouteHelper()

    await $fetch('/api/cl/oob/sendCheckoutEmail', {
      method: 'POST',
      body: {
        orderId,
        oobCustomerEmail: oobCustomerEmail.value,
        country,
        locale: countryAndLanguage,
        language: checkoutLanguage,
      },
    })
  }

  const activateOobCheckoutSession = (
    accessToken: string,
    customerEmail: string,
    orderId?: string
  ) => {
    writeStoredOobCheckoutAccessToken(accessToken)
    if (orderId) {
      writeStoredOobCheckoutOrderId(orderId)
    }
    // Use the real expiry from the JWT `exp` claim so isTokenValid() reflects
    // the actual Commerce Layer token lifetime (no fabricated TTL).
    const expires =
      getCommerceLayerJwtExpiry(accessToken) ??
      new Date(Date.now() + 55 * 60 * 1000)

    onBehalfToken.value = {
      accessToken,
      tokenType: 'bearer',
      expires,
    } as unknown as Token

    oobCustomerEmail.value = customerEmail

    onBehalfState.value = {
      isActive: true,
      targetCustomerEmail: customerEmail,
      targetCustomerId: null,
      agentEmail: null,
    }
  }

  /**
   * Validates the sales-channel JWT (mfe-checkout `accessToken` query param)
   * and activates the OOB checkout session.
   */
  const applyCheckoutAccessToken = async (
    accessToken: string,
    expectedOrderId?: string
  ): Promise<{
    orderId: string
    accessToken: string
    operatorName: string
    operatorEmail: string
    operatorTag: string
  }> => {
    const qs = new URLSearchParams({ accessToken })
    if (expectedOrderId) {
      qs.set('orderId', expectedOrderId)
    }

    const result = await $fetch<{
      orderId: string
      accessToken: string
      operatorName: string
      operatorEmail: string
      operatorTag: string
      customerEmail: string
      error?: string
    }>(`/api/cl/oob/resolveToken?${qs.toString()}`)

    if (result.error) {
      throw Object.assign(new Error(result.error), { code: result.error })
    }

    activateOobCheckoutSession(
      result.accessToken,
      result.customerEmail,
      result.orderId
    )

    return {
      orderId: result.orderId,
      accessToken: result.accessToken,
      operatorName: result.operatorName,
      operatorEmail: result.operatorEmail,
      operatorTag: result.operatorTag,
    }
  }

  const resetOobSession = () => {
    oobCustomerEmail.value = ''
    oobCustomerCLId.value = null
    oobCustomerCrmId.value = null
    oobCustomerIsRegistered.value = false
    includeInOrderHistory.value = false
    otpSent.value = false
    otpVerified.value = false
    error.value = null
    onBehalfToken.value = null
    onBehalfState.value = {
      isActive: false,
      targetCustomerEmail: null,
      targetCustomerId: null,
      agentEmail: null,
    }
    clearStoredOobCheckoutAccessToken()
  }

  const oobCartSharedMessage = useState<string | null>(
    'oobCartSharedMessage',
    () => null
  )

  const setOobCartSharedMessage = () => {
    oobCartSharedMessage.value = ts('checkout.oob.cartOrderShared')
  }

  const clearOobCartSharedMessage = () => {
    oobCartSharedMessage.value = null
  }

  return {
    // Operator detection
    isOperatorMode,
    isOperatorModeResolved: readonly(isOperatorModeResolved),
    operatorTag,
    canOrderOnBehalf, // backward compat alias
    // OOB session state (readonly)
    oobCustomerEmail: readonly(oobCustomerEmail),
    oobCustomerCLId: readonly(oobCustomerCLId),
    oobCustomerCrmId: readonly(oobCustomerCrmId),
    oobCustomerIsRegistered: readonly(oobCustomerIsRegistered),
    includeInOrderHistory,
    otpSent: readonly(otpSent),
    otpVerified: readonly(otpVerified),
    // Backward compat (used by useCL.ts)
    onBehalfState: readonly(onBehalfState),
    onBehalfToken: readonly(onBehalfToken),
    getToken,

    // Loading / error
    isLoading: readonly(isLoading),
    error: readonly(error),

    // Methods
    initOperatorMode,
    skipOperatorModeForGuestCheckout,
    searchOobCustomer,
    verifyOtp,
    applyOobOrderHistoryMetadata,
    completeOobCustomerSearchStep,
    generateShareableLink,
    sendCheckoutEmailToClient,
    applyCheckoutAccessToken,
    readStoredOobCheckoutAccessToken,
    resetOobSession,
    oobCartSharedMessage: readonly(oobCartSharedMessage),
    setOobCartSharedMessage,
    clearOobCartSharedMessage,
  }
}
